import { Euler, Matrix4, Quaternion, Vector3 } from "flavr-math";
import { convertToNumber3, DeleteModelMessage, ExitCurrentRoomMessage, ImageFacet, InteractionFacet, CollisionFacet, PrimitiveShape, SendMessagesAction, ShowTooltipsBehavior, TooltipTextAlign, TooltipInfoFacet, TooltipPlacement, LocalTransformFacet, UpdateFacetOnModelMessage, VisibilityFacet, UTILITY_ROOM_UID, SelectionHighlightFacet, } from "flavr-common";
import { UpdateCollisionShapeLocalTransformMessage } from "@messages/selection";
import { modelPool, useExternalMessages } from "@modules/model";
import { StartPointSystem } from "./StartPointSystem";
import { RoomChangeType } from "../renderable/RendererModelInterface";
import { FacetRenderer } from "../renderable/Renderable";
import { RotatingRenderable } from "@modules/rotatingMesh/RotatingRenderable";
import { camera } from "../camera";
const EXIT_POINT_PRE_NAME = "__exit_to_parent_room_";
const IMAGE_SNAP_YAW = Math.PI / 8;
export class StartPointRenderable extends FacetRenderer {
    constructor(owner) {
        super(owner);
        this.owner = owner;
        this.isVisible = true;
        this.deleted = false;
        this.colliderUnitScale = new Vector3(1, 1, 1);
        this.colliderLocalPosition = new Vector3();
        this.colliderRotationAxis = new Vector3(0, 1, 0);
        this.cameraWorldPosition = new Vector3();
        this.lastCameraWorldMatrix = new Matrix4();
        this.roomSpaceDirection = new Vector3();
        this.worldDirection = new Vector3();
        this.modelEuler = new Euler();
        this.roomSpaceInstanceCenter = new Vector3();
        this.renderableWorldPosition = new Vector3();
        this.renderableWorldOrientation = new Quaternion();
        this.colliderLocalOrientation = new Quaternion();
        this.colliderLocalTransform = new Matrix4();
        this.hasLastCameraWorldMatrix = false;
        this.updateColliderLocalTransformOnCameraChange = () => {
            const currentCameraWorldMatrix = camera.perspectiveCam.matrixWorld;
            if (this.hasLastCameraWorldMatrix && this.lastCameraWorldMatrix.equals(currentCameraWorldMatrix))
                return;
            this.lastCameraWorldMatrix.copy(currentCameraWorldMatrix);
            this.hasLastCameraWorldMatrix = true;
            this.updateColliderLocalTransform();
        };
        this.updateColliderLocalTransform = () => {
            if (this.renderableModel === undefined || this.deleted)
                return;
            const renderableModelInterface = this.owner.renderer.getModel(this.renderableModel.getUID());
            if (renderableModelInterface === undefined || !renderableModelInterface.matrixWorldValid)
                return;
            this.cameraWorldPosition.setFromMatrixPosition(camera.perspectiveCam.matrixWorld);
            this.renderableWorldPosition.copy(renderableModelInterface.worldPosition);
            this.renderableWorldOrientation.copy(renderableModelInterface.worldOrientation);
            this.modelEuler.setFromQuaternion(this.renderableWorldOrientation, "YZX");
            const roomUID = this.renderableModel.getRoomOrThrow().getUID();
            const roomMatrix = this.owner.renderer.getRoomTransformMatrix(roomUID);
            this.roomSpaceInstanceCenter.copy(this.renderableWorldPosition).applyMatrix4(roomMatrix);
            this.worldDirection.subVectors(this.cameraWorldPosition, this.roomSpaceInstanceCenter);
            this.applyDirectionRowMatrix(this.worldDirection, roomMatrix, this.roomSpaceDirection);
            const modelYaw = this.modelEuler.y;
            const targetYaw = Math.atan2(this.roomSpaceDirection.x, this.roomSpaceDirection.z);
            const snappedYaw = modelYaw + Math.floor(((targetYaw - modelYaw) / IMAGE_SNAP_YAW) + 0.5) * IMAGE_SNAP_YAW;
            const localYaw = snappedYaw - modelYaw;
            if (this.lastColliderLocalYaw === localYaw)
                return;
            this.lastColliderLocalYaw = localYaw;
            this.colliderLocalOrientation.setFromAxisAngle(this.colliderRotationAxis, localYaw);
            this.colliderLocalTransform.compose(this.colliderLocalPosition, this.colliderLocalOrientation, this.colliderUnitScale);
            this.system.connection.send(new UpdateCollisionShapeLocalTransformMessage(this.renderableModel.getUID(), this.colliderLocalTransform.toArray()));
        };
        this.system = this.owner.renderer.getSystem(StartPointSystem);
        this.subscribeRoomChange();
    }
    destroy() {
        this.deleteExitPointModel();
        this.system.unregister(this);
    }
    setVisible(visible) {
        this.isVisible = visible;
        if (this.renderableModel !== undefined && !this.deleted) {
            this.system.connection.send(new UpdateFacetOnModelMessage(this.renderableModel.getUID(), new VisibilityFacet(this.isVisible)));
        }
    }
    onInit() {
        this.system.register(this);
    }
    updateFacet(_facet) { }
    updateTransform() {
        var _a, _b;
        if (this.renderableModel !== undefined && !this.deleted) {
            const defaultHeight = (_b = (_a = this.facet) === null || _a === void 0 ? void 0 : _a.params.defaultHeight) !== null && _b !== void 0 ? _b : 1;
            const exitPointPosition = this.applyRoomRotationToWorldPosition(this.owner.worldPosition.clone().add(new Vector3(0, defaultHeight * 0.5, 0)));
            this.owner.renderer
                .getSystem(StartPointSystem)
                .connection.send(new UpdateFacetOnModelMessage(this.renderableModel.getUID(), new LocalTransformFacet(convertToNumber3(exitPointPosition), [0, 0, 0, 1], [0.5, 0.5, 0.5])));
            this.updateColliderLocalTransform();
        }
    }
    onRoomChanged(type) {
        if (type !== RoomChangeType.IN_CURRENT) {
            this.deleteExitPointModel();
        }
    }
    createExitPointModel(roomName) {
        var _a, _b;
        if (!this.owner.isModelInCurrentRoom())
            return;
        const messages = [];
        const utilityRoom = modelPool.getModelOrThrow(UTILITY_ROOM_UID);
        this.renderableModel = utilityRoom.getChild(`${EXIT_POINT_PRE_NAME}${roomName}`);
        if (this.renderableModel === undefined) {
            this.renderableModel = modelPool.createModel(`${EXIT_POINT_PRE_NAME}${roomName}`, utilityRoom, useExternalMessages(messages));
        }
        this.deleted = false;
        this.lastColliderLocalYaw = undefined;
        const defaultHeight = (_b = (_a = this.facet) === null || _a === void 0 ? void 0 : _a.params.defaultHeight) !== null && _b !== void 0 ? _b : 1;
        const exitPointPosition = this.applyRoomRotationToWorldPosition(this.owner.worldPosition.clone().add(new Vector3(0, defaultHeight * 0.5, 0)));
        messages.push(new UpdateFacetOnModelMessage(this.renderableModel.getUID(), new VisibilityFacet(this.isVisible)), new UpdateFacetOnModelMessage(this.renderableModel.getUID(), new CollisionFacet(PrimitiveShape.Plane, true)), new UpdateFacetOnModelMessage(this.renderableModel.getUID(), new SelectionHighlightFacet(true)), new UpdateFacetOnModelMessage(this.renderableModel.getUID(), new ImageFacet("icons/ExitFlaVR.png", {
            snapYaw: IMAGE_SNAP_YAW
        })), new UpdateFacetOnModelMessage(this.renderableModel.getUID(), new TooltipInfoFacet({ key: "ui.exitRoom" }, TooltipTextAlign.Left, undefined, TooltipPlacement.LEFT_OF_MODEL)), new UpdateFacetOnModelMessage(this.renderableModel.getUID(), new InteractionFacet({
            behaviors: [new ShowTooltipsBehavior()],
            onClick: [
                new SendMessagesAction([
                    new DeleteModelMessage(this.renderableModel.getUID()),
                    new ExitCurrentRoomMessage()
                ])
            ]
        })), new UpdateFacetOnModelMessage(this.renderableModel.getUID(), new LocalTransformFacet(convertToNumber3(exitPointPosition), [0, 0, 0, 1], [0.5, 0.5, 0.5])));
        this.system.connection.sendMultiple(messages);
        this.registerAnimationCallback(this.updateColliderLocalTransformOnCameraChange);
        this.updateColliderLocalTransform();
    }
    deleteExitPointModel() {
        if (this.renderableModel === undefined || this.deleted)
            return;
        this.deleted = true;
        this.lastColliderLocalYaw = undefined;
        this.hasLastCameraWorldMatrix = false;
        this.unregisterAnimationCallback();
        this.system.connection.send(new DeleteModelMessage(this.renderableModel.getUID()));
    }
    applyDirectionRowMatrix(worldDirection, matrix, out) {
        const e = matrix.elements;
        out.set(worldDirection.x * e[0] + worldDirection.y * e[1] + worldDirection.z * e[2], worldDirection.x * e[4] + worldDirection.y * e[5] + worldDirection.z * e[6], worldDirection.x * e[8] + worldDirection.y * e[9] + worldDirection.z * e[10]);
        const length = out.length();
        if (length > 0)
            out.multiplyScalar(1 / length);
    }
    applyRoomRotationToWorldPosition(staticPosition) {
        let current = modelPool.getModel(this.owner.modelUID);
        while (current !== undefined && !current.isRoot()) {
            const modelInterface = this.owner.renderer.getModel(current.getUID());
            if (modelInterface !== undefined) {
                const rotatingRenderable = modelInterface.getFacetRenderer(RotatingRenderable);
                if (rotatingRenderable !== undefined) {
                    const accumulatedLocal = rotatingRenderable.getAccumulatedRotation();
                    const rotatingModelOrientation = modelInterface.worldOrientation;
                    const accumulatedWorld = rotatingModelOrientation.clone()
                        .multiply(accumulatedLocal)
                        .multiply(rotatingModelOrientation.clone().invert());
                    const rotatingModelCenter = modelInterface.worldPosition;
                    const relativeToCenter = staticPosition.clone().sub(rotatingModelCenter);
                    relativeToCenter.applyQuaternion(accumulatedWorld);
                    return relativeToCenter.add(rotatingModelCenter);
                }
            }
            current = current.getParent();
        }
        return staticPosition;
    }
}
